import React, { useState, useEffect, useMemo, useCallback, useDeferredValue } from 'react';
import { Search } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FilterPanel } from '../components/gallery/FilterPanel';
import { GallerySection } from '../components/gallery/GallerySection';
import { GalleryLightbox } from '../components/gallery/GalleryLightbox';
import { NextStep } from '../components/NextStep';
import { prepareRows, readHash, writeHash, matches, chipCounts, uniqCount, groupByCategory, emptyFilters, TASK, ENV } from '../lib/gallery';

const PANELS = [["env", "Environments", 80], ["task", "Tasks", 100], ["obj", "Objects & tools", 120]];

const Stats = ({ rows }) => (
  <div className="grid grid-cols-3 gap-2 mb-5" data-testid="gallery-stats">
    {[[uniqCount(rows, (r) => [ENV(r)]), "environments"], [uniqCount(rows, (r) => [TASK(r)]), "tasks"], [uniqCount(rows, (r) => r._objs), "objects & tools"]].map(([b, s]) => (
      <div key={s} className="bg-[#141414] border border-white/10 rounded-xl px-4 py-3" data-testid={`stat-${s.split(' ')[0]}`}>
        <div><b className="font-mono text-2xl lg:text-3xl font-bold gradient-text">{Number(b).toLocaleString()}</b></div>
        <span className="text-xs text-white/40">{s}</span>
      </div>
    ))}
  </div>
);

const DatasetCatalogue = () => {
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState(readHash);
  const [qInput, setQInput] = useState(filters.q);
  const [cur, setCur] = useState(null);
  const f = useDeferredValue(filters);

  useEffect(() => {
    fetch('/gallery/gallery.json').then((r) => r.json()).then((d) => setRows(prepareRows(d)));
  }, []);

  useEffect(() => { writeHash(filters); }, [filters]);

  useEffect(() => {
    const h = () => { const nf = readHash(); setFilters(nf); setQInput(nf.q); };
    window.addEventListener('hashchange', h);
    return () => window.removeEventListener('hashchange', h);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setFilters((p) => (p.q === qInput.trim() ? p : { ...p, q: qInput.trim() })), 150);
    return () => clearTimeout(t);
  }, [qInput]);

  const toggle = useCallback((k, v) => setFilters((p) => {
    const s = new Set(p[k]);
    s.has(v) ? s.delete(v) : s.add(v);
    return { ...p, [k]: s };
  }), []);

  const clear = () => { setQInput(''); setFilters(emptyFilters()); };

  const shown = useMemo(() => (rows ? rows.filter((r) => matches(r, f)) : []), [rows, f]);
  const indexOf = useMemo(() => new Map(shown.map((r, i) => [r.id, i])), [shown]);
  const groups = useMemo(() => groupByCategory(shown), [shown]);
  const chips = useMemo(() => (rows ? Object.fromEntries(PANELS.map(([k, , l]) => [k, chipCounts(rows, f, k, l)])) : {}), [rows, f]);

  const go = useCallback((d) => setCur((c) => (c + d + shown.length) % shown.length), [shown.length]);
  const prev = useCallback(() => go(-1), [go]);
  const next = useCallback(() => go(1), [go]);
  const close = useCallback(() => setCur(null), []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Header />
      <main className="max-w-[1440px] mx-auto px-4 lg:px-8 pt-24 lg:pt-32 pb-16 min-w-0" data-testid="data-diversity-gallery">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-2" data-testid="gallery-title">Data Showcase</h1>
        <p className="text-sm md:text-base text-white/45 max-w-3xl mb-8" data-testid="gallery-subtitle">
          India is the world's most varied workshop, and we record it from the inside. Through a network of 57M+ workers across the country, we capture real work wherever it happens: from salon chairs to soldering benches, farm fields to warehouse floors, roadside repair stalls to modern factory lines. Dim back kitchens and bright assembly halls, gloved hands and bare ones, delicate tweezers and heavy presses, all in the messy, unscripted conditions no lab can recreate, and at 2–3x lower cost, thanks to India's cost advantage. This is the long tail of the physical world your robots will meet. Every face is blurred for privacy. Tap a filter to explore, then click any photo to see the detail behind it.
        </p>

        {!rows ? (
          <p className="text-white/40 py-20 text-center" data-testid="gallery-loading">Loading…</p>
        ) : (
          <>
            <Stats rows={shown} />
            <div className="grid gap-3 mb-4 grid-cols-[repeat(auto-fit,minmax(300px,1fr))]">
              {PANELS.map(([k, title]) => (
                <FilterPanel key={k} k={k} title={title} chips={chips[k]} selected={filters[k]} onToggle={toggle} />
              ))}
            </div>

            <div className="sticky top-16 lg:top-20 z-30 bg-[#0A0A0A]/90 backdrop-blur-md py-3 mb-6 flex flex-wrap gap-2 items-center border-b border-white/10" data-testid="gallery-filter-bar">
              <div className="relative flex-1 basis-56 min-w-0">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                <input
                  type="search" value={qInput} onChange={(e) => setQInput(e.target.value)}
                  placeholder="Search objects, actions, captions…" aria-label="Search"
                  className="w-full bg-[#141414] border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#FF7A00]/60 transition-colors"
                  data-testid="gallery-search-input"
                />
              </div>
              <button type="button" onClick={clear} data-testid="gallery-clear-filters"
                className="rounded-full border border-white/10 px-4 py-2 text-sm text-white/80 hover:border-[#FF7A00] hover:text-[#FF7A00] transition-colors">
                Clear filters
              </button>
              <span className="ml-auto font-mono text-xs text-white/40 whitespace-nowrap" data-testid="gallery-count">{shown.length.toLocaleString()} / {rows.length.toLocaleString()}</span>
            </div>

            <div className={`transition-opacity duration-200 ${f !== filters ? 'opacity-40' : ''}`} data-testid="gallery-results">
              {shown.length ? (
                groups.map(([c, rs]) => <GallerySection key={c} category={c} rows={rs} indexOf={indexOf} onOpen={setCur} />)
              ) : (
                <p className="text-white/40 py-10 text-center" data-testid="gallery-empty">No photos match these filters.</p>
              )}
            </div>
            <NextStep kicker="Next" title="See how this data is captured" text="The six-camera cap, labels, quality checks and model benchmarks behind every recording." to="/technology/capture" testid="showcase-next-technology" />
          </>
        )}
      </main>
      {cur !== null && shown[cur] && <GalleryLightbox r={shown[cur]} onPrev={prev} onNext={next} onClose={close} />}
      <Footer />
    </div>
  );
};

export default DatasetCatalogue;
